/**
 * AI画像生成 SSE API
 */
import { fetchAuthToken } from "app/Auth";
import Constants from "common/constant";
import type {
  GenerateImageParams,
  GenerateMaterialImageParams,
  ImageGenerationSSECallbacks,
  AIGeneratedAsset,
  AIImageUsageInfo,
} from "../types";

/**
 * SSEイベントハンドラ
 */
const handleImageSSEEvent = (
  event: string,
  data: Record<string, unknown>,
  callbacks: ImageGenerationSSECallbacks,
) => {
  switch (event) {
    case "generation_start":
      callbacks.onStart(data.requestId as string);
      break;
    case "partial_image":
      callbacks.onPartialImage(
        data.b64Json as string,
        data.progress as number,
        data.index as number,
        data.imageIndex as number | undefined,
      );
      break;
    case "generation_complete": {
      // 個別画像完了（metadata内にfileSizeはない場合がある）
      const imageIndex = data.imageIndex as number | undefined;
      callbacks.onComplete(
        data.imageUrl as string,
        data.id as number,
        0, // fileSizeはall_completeで取得
        imageIndex,
      );
      break;
    }
    case "all_complete": {
      // 全画像完了
      const images = data.images as AIGeneratedAsset[];
      const usage = data.usage as AIImageUsageInfo | undefined;
      callbacks.onAllComplete?.(images, usage);
      break;
    }
    case "error":
      callbacks.onError(data.code as string, data.message as string);
      break;
  }
};

/**
 * AI画像生成（SSE Stream）
 * UIDがない場合はプロフィール用エンドポイントを使用（アセット保存なし）
 */
export const generateImageSSE = async ({
  uid,
  options,
  purpose = "child_profile_icon",
  callbacks,
  signal,
}: GenerateImageParams): Promise<void> => {
  const token = await fetchAuthToken();
  // UIDがない場合はプロフィール用エンドポイントを使用
  const url = uid
    ? `${Constants.apiHost}/v1/ai/image/users/${uid}/generate`
    : `${Constants.apiHost}/v1/ai/image/generate`;

  const body = {
    purpose,
    params: {
      gender: options.gender || "none",
      age: options.age,
      // 子供用フィールド
      favorite_colors: options.favorite_colors,
      self_introduction: options.self_introduction,
      best_subjects: options.best_subjects,
      // 容姿フィールド
      glasses: options.glasses,
      accessory: options.accessory,
      hairstyle: options.hairstyle,
      body_type: options.body_type,
      face_shape: options.face_shape,
      // 保護者用フィールド
      occupation: options.occupation,
      job_title: options.job_title,
      organization: options.organization,
      region: options.region,
      city: options.city,
      household_members: options.household_members,
      boys_count: options.boys_count,
      girls_count: options.girls_count,
      industry: options.industry,
      annual_income: options.annual_income,
      // 追加プロンプト（再生成時）
      additional_prompt: options.additional_prompt,
    },
    partialImages: 3,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    // 403エラーの場合、レスポンスボディからエラーコードを抽出
    if (response.status === 403) {
      try {
        const body = await response.json();
        callbacks.onError(
          body.code || "ACCESS_DENIED",
          body.message || "この機能はご利用いただけません",
        );
      } catch {
        callbacks.onError("ACCESS_DENIED", "この機能はご利用いただけません");
      }
      return;
    }
    callbacks.onError("HTTP_ERROR", `HTTP error: ${response.status}`);
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError("NO_READER", "Response body is not readable");
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7);
        } else if (line.startsWith("data: ")) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            handleImageSSEEvent(currentEvent, parsed, callbacks);
          } catch {
            // JSON parse error, skip
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};

/**
 * 素材画像生成（SSE Stream）- 自由テキスト入力
 */
export const generateMaterialImageSSE = async ({
  uid,
  options,
  callbacks,
  signal,
}: GenerateMaterialImageParams): Promise<void> => {
  const token = await fetchAuthToken();
  const url = `${Constants.apiHost}/v1/ai/image/users/${uid}/generate`;

  const body = {
    purpose: "material_image",
    params: {
      prompt: options.prompt,
    },
    partialImages: 3,
    imageCount: options.imageCount || 1,
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: token,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    if (response.status === 403) {
      try {
        const respBody = await response.json();
        callbacks.onError(
          respBody.code || "ACCESS_DENIED",
          respBody.message || "この機能はご利用いただけません",
        );
      } catch {
        callbacks.onError("ACCESS_DENIED", "この機能はご利用いただけません");
      }
      return;
    }
    if (response.status === 429) {
      try {
        const respBody = await response.json();
        callbacks.onError(
          respBody.code || "QUOTA_EXCEEDED",
          respBody.message || "本日の生成回数の上限に達しました",
        );
      } catch {
        callbacks.onError("QUOTA_EXCEEDED", "本日の生成回数の上限に達しました");
      }
      return;
    }
    callbacks.onError("HTTP_ERROR", `HTTP error: ${response.status}`);
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError("NO_READER", "Response body is not readable");
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7);
        } else if (line.startsWith("data: ")) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            handleImageSSEEvent(currentEvent, parsed, callbacks);
          } catch {
            // JSON parse error, skip
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};
